/**
 * Who is signed in and what they may see in the current tenant.
 *
 * Loads /api/auth/me for the tenant in the URL. A 401 anywhere (this request
 * or any API call, via AUTH_REQUIRED) sends the user to /login and back here
 * after signing in. If the URL names a tenant the user cannot open, it is
 * switched to one they can. The server enforces every permission; this only
 * decides what to show.
 */

import { createContext, useContext, useEffect, useMemo } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AUTH_REQUIRED, apiPost } from "../api/client.js";
import { useMe } from "../api/hooks.js";
import { useFilters } from "../lib/useFilters.js";
import { StatePanel } from "../components/primitives.jsx";
import { Button, Spinner } from "../components/ui/index.js";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const { filters, setTenant } = useFilters();
  const { data, error, isLoading } = useMe(filters.tenant);

  // Any 401 → sign in, then come back to this page.
  useEffect(() => {
    const toLogin = () => {
      const next = `${location.pathname}${location.search}`;
      qc.clear();
      navigate(`/login?${new URLSearchParams({ next })}`, { replace: true });
    };
    window.addEventListener(AUTH_REQUIRED, toLogin);
    return () => window.removeEventListener(AUTH_REQUIRED, toLogin);
  }, [navigate, location.pathname, location.search, qc]);

  // The URL names a tenant this user cannot open → use the one the server picked.
  useEffect(() => {
    if (data?.tenant && data.tenant !== filters.tenant) setTenant(data.tenant);
  }, [data?.tenant, filters.tenant, setTenant]);

  const value = useMemo(() => {
    const granted = new Set(data?.permissions ?? []);
    return {
      me: data ?? null,
      loading: isLoading,
      /** True when the signed-in user has `permission` in the current tenant. */
      can: (permission) => granted.has(permission),
      signOut: async () => {
        await apiPost("/auth/logout").catch(() => {});
        qc.clear();
        navigate("/login", { replace: true });
      },
    };
  }, [data, isLoading, qc, navigate]);

  if (isLoading || (data?.tenant && data.tenant !== filters.tenant)) {
    return (
      <div className="flex min-h-screen animate-fade-in flex-col items-center justify-center gap-3 text-ink-3" role="status">
        <Spinner className="size-6 text-accent" />
        <p className="text-[12.5px]">Checking your access…</p>
      </div>
    );
  }
  if (error?.kind === "auth") {
    return <Navigate replace to={`/login?${new URLSearchParams({ next: `${location.pathname}${location.search}` })}`} />;
  }
  if (error) {
    return <StatePanel icon="alert" className="mt-24" detail={`Could not check your access: ${error.message}`} />;
  }
  if (data && !data.tenant) {
    return (
      <StatePanel
        className="mt-24"
        title="No analytics access"
        detail="Your account has no analytics permissions for any tenant. Ask an administrator to grant one."
        action={
          <Button icon="logout" onClick={value.signOut}>
            Sign out
          </Button>
        }
      />
    );
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
